import React, { ChangeEvent, createRef, useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import InputField from '../../../form/inputField';
import PopoverContainer from '../../../popover/popover';
import DropDownField from '../../../form/dropdownField';
import getRoles from '../../../../helpers/role';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import { ELanguage } from '../../utils/language';
import { IAccountModel } from '../../../../helpers/models/accountModel';
import { ProfessionalStep2FormResultKeys } from './professionalStep2FormModel';
import { IFormModalRef } from '../../../modal/modal';
import FormEligibilityModal, { IFormEligibilityModalRef } from '../../../modal/instances/eligibility/formEligibilityModal';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import getProvinces from '../../../../helpers/provinces';
import { handleInputWithLabelOnBlur, handleInputWithLabelOnFocus } from '../../registration';
import {
    FORM_FIRSTNAME_MAX_LENGTH,
    FORM_LASTNAME_MAX_LENGTH,
    FORM_PHONE_EXT_MAX_LENGTH,
    FORM_PHONE_MAX_LENGTH,
    validatePhoneNumberFormat,
} from '../../utils/formValidation';

export interface IProfessionalStep2ProviderProfileProps extends WithTranslation {
    account: IAccountModel;
    setProviderProps?: any;
    readonly?: boolean;
    updateStep?: any;
}

const ProfessionalStep2ProviderProfile = (props: IProfessionalStep2ProviderProfileProps) => {
    const { t, setProviderProps, readonly, updateStep, account, i18n } = props;

    const [roles, setRoles] = useState<IRoleModel[]>([]);
    const [provinces, setProvinces] = useState<IProvinceModel[]>([]);

    const [language, setLanguage] = useState<boolean>(false);
    const eligibilityRef = createRef<IFormModalRef & IFormEligibilityModalRef>();

    useEffect(() => {
        setLanguage((account.provider[0]?.lng ?? i18n.language) === 'en');
    }, [account.provider[0]?.lng, i18n.language]);

    useEffect(() => {
        async function getData() {
            let roleList = await getRoles(i18n.language);
            if (roleList) {
                const accountRole = account.role as IRoleModel;
                if (accountRole.listSkipCodes) {
                    const skipCodes = accountRole.listSkipCodes?.split(',');
                    roleList = roleList.filter((role: IRoleModel) => {
                        return !skipCodes?.includes(role.code);
                    });
                }
            }
            setRoles(roleList ?? []);

            const provinceList = await getProvinces(i18n.language);
            setProvinces(provinceList ?? []);
        }
        getData();
    }, [i18n.language]);

    const roleComponents = roles.map((role: IRoleModel) => {
        return (
            <option key={role.id} value={role.id}>
                {role.name}
            </option>
        );
    });

    const handleOnChangeProvider = (e: ChangeEvent<HTMLSelectElement>) => {
        setProviderProps(+e.target.value);
    };

    return (
        <>
            <FormEligibilityModal provinces={provinces} roles={roles} ref={eligibilityRef} />
            <div className="registration-professional-step-2-header">
                <h1 className="header-3 header-3--purple registration_form_header">
                    {t('form_professional_step_2_title')}
                </h1>
                {readonly && (
                    <a
                        data-step={2}
                        className="standalone-link registration_form--modify-button"
                        tabIndex={0}
                        role="button"
                        onClick={updateStep}
                        aria-label={t('form_professional_step_2_title')
                            .addSpace()
                            .concat(t('form_revise_submit_professional_step_2_modify_btn'))}
                    >
                        {t('form_revise_submit_professional_step_2_modify_btn')}
                        <span className="registration-professional-step-2--modify-button"></span>
                    </a>
                )}
            </div>
            <div className="registration-form-container">
                <div className="registration-section">
                    <div className="registration-column-container">
                        <InputField
                            fieldContainerClassName="registration-column-item"
                            fieldName={ProfessionalStep2FormResultKeys.FIRST_NAME}
                            fieldTitle="form_professional_step_2_first_name"
                            fieldDisabled={readonly}
                            type="text"
                            fieldRequired
                            fieldHiddenLabel="form_professional_step_2_title"
                            defaultValue={account.provider[0]?.firstname ?? account.firstname}
                            fieldWeight={0}
                            maxLength={FORM_FIRSTNAME_MAX_LENGTH}
                        />
                        <InputField
                            fieldContainerClassName="registration-column-item"
                            fieldName={ProfessionalStep2FormResultKeys.LAST_NAME}
                            fieldTitle="form_professional_step_2_last_name"
                            fieldDisabled={readonly}
                            type="text"
                            fieldHiddenLabel="form_professional_step_2_title"
                            fieldRequired
                            defaultValue={account.provider[0]?.lastname ?? account.lastname}
                            fieldWeight={1}
                            maxLength={FORM_LASTNAME_MAX_LENGTH}
                        />
                    </div>
                    <div className="registration-column-container">
                        <InputField
                            fieldContainerClassName="registration-field registration-contact-info-phone-field registration-column-item"
                            fieldName={ProfessionalStep2FormResultKeys.PHONE}
                            fieldTitle="form_professional_step_2_field_phone"
                            fieldDisabled={readonly}
                            fieldRequired
                            fieldHiddenLabel="form_professional_step_2_title"
                            type="text"
                            placeholder="999-999-9999"
                            defaultValue={account.provider[0]?.phone ?? account.phone}
                            maxLength={FORM_PHONE_MAX_LENGTH}
                            handleOnChange={validatePhoneNumberFormat}
                            fieldWeight={2}
                        />
                        <InputField
                            fieldContainerClassName="registration-field registration-column-item registration-contact-info-ext-field"
                            fieldName={ProfessionalStep2FormResultKeys.PHONE_EXT}
                            fieldTitle="form_professional_step_2_field_phone_ext"
                            fieldOptionalText="form_professional_step_2_field_phone_ext_optional"
                            fieldDisabled={readonly}
                            fieldHiddenLabel="form_professional_step_2_title"
                            inputMode="numeric"
                            defaultValue={account.provider[0]?.ext ?? account.ext}
                            fieldWeight={3}
                            maxLength={FORM_PHONE_EXT_MAX_LENGTH}
                            handleOnChange={validatePhoneNumberFormat}
                        />
                    </div>
                    <div className="registration-column-container">
                        <InputField
                            fieldContainerClassName="registration-field form_professional_step_2_field_email"
                            fieldName={ProfessionalStep2FormResultKeys.EMAIL}
                            fieldTitle="form_professional_step_2_field_email"
                            fieldDisabled={readonly}
                            fieldHiddenLabel="form_professional_step_2_title"
                            fieldWeight={4}
                            fieldTooltip={
                                <PopoverContainer
                                    icon
                                    className={'field-tooltip-icon'}
                                    prefix={t('form_professional_step_2_field_email')}
                                >
                                    <p className="body-medium">
                                        {t('form_professional_step_2_field_email_popover')}
                                    </p>
                                </PopoverContainer>
                            }
                            fieldRequired
                            type="email"
                            defaultValue={account.provider[0]?.email ?? account.email}
                        />
                    </div>
                </div>
                <div className="registration-section">
                    <div className="registration-professional-profession">
                        <div>
                            <DropDownField
                                fieldContainerClassName="field-container--large registration-column-item"
                                fieldName={ProfessionalStep2FormResultKeys.PROFESSION}
                                fieldTitle="form_professional_step_2_profession"
                                fieldDefaultValue="form_professional_step_2_profession_default"
                                fieldOptions={roleComponents}
                                fieldHiddenLabel="form_professional_step_2_title"
                                fieldDisabled={readonly}
                                fieldRequired
                                fieldWeight={5}
                                value={
                                    (account.provider[0]?.role as IRoleModel)?.id ??
                                    (account.role as IRoleModel).id
                                }
                                handleOnChange={handleOnChangeProvider}
                            />
                        </div>
                        {!readonly && (
                            <div className="body-medium">
                                <div>{t('form_professional_step_2_profession_not_listed')}</div>
                                <a
                                    onClick={() => eligibilityRef.current?.setModalOpen(true)}
                                    className="standalone-link standalone-link--large"
                                    tabIndex={0}
                                >
                                    {t('form_professional_step_2_profession_eligibility_condition')}
                                    .
                                </a>
                            </div>
                        )}
                    </div>
                    <div className="registration-column-container registration-preferred-lang-container">
                        <div className="body-medium">
                            {t('form_professional_step_2_correspondence_lang_title')}
                        </div>
                        <div className="registration-preferred-lang-selection">
                            <div className="radio-button-container">
                                <input
                                    className="radio-button--input field-value"
                                    type="radio"
                                    value={ELanguage.English}
                                    id="form_professional_step_2_correspondence_lang_english"
                                    name={ProfessionalStep2FormResultKeys.LNG}
                                    onBlur={handleInputWithLabelOnBlur}
                                    onFocus={handleInputWithLabelOnFocus}
                                    disabled={readonly}
                                    checked={language}
                                    onChange={(e) => {
                                        e.target.checked && setLanguage(true);
                                    }}
                                />
                                <label
                                    htmlFor="form_professional_step_2_correspondence_lang_english"
                                    className={'radio-button radio-button--no-margin field-label registration-column-item '.concat(
                                        readonly ? 'field-label--disabled' : ''
                                    )}
                                >
                                    <div className="accessibility-hidden">
                                        {t('form_professional_step_2_correspondence_lang_title')}
                                    </div>
                                    {t('form_professional_step_2_correspondence_lang_english')}
                                </label>
                            </div>
                            <div className="radio-button-container">
                                <input
                                    className="radio-button--input field-value"
                                    type="radio"
                                    value={ELanguage.French}
                                    id="form_professional_step_2_correspondence_lang_french"
                                    name={ProfessionalStep2FormResultKeys.LNG}
                                    onBlur={handleInputWithLabelOnBlur}
                                    onFocus={handleInputWithLabelOnFocus}
                                    disabled={readonly}
                                    checked={!language}
                                    onChange={(e) => {
                                        e.target.checked && setLanguage(false);
                                    }}
                                />
                                <label
                                    htmlFor="form_professional_step_2_correspondence_lang_french"
                                    className={'radio-button radio-button--no-margin field-label registration-column-item '.concat(
                                        readonly ? 'field-label--disabled' : ''
                                    )}
                                >
                                    <div className="accessibility-hidden">
                                        {t('form_professional_step_2_correspondence_lang_title')}
                                    </div>
                                    {t('form_professional_step_2_correspondence_lang_french')}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default withTranslation()(ProfessionalStep2ProviderProfile);
